// Fichier: src/components/dashboard/AdminEventDetails.js

import React, { useState, useEffect } from "react";
import { doc, updateDoc, deleteDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";

const AdminEventDetails = ({ event, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [clients, setClients] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [admins, setAdmins] = useState([]);
  
  const [formData, setFormData] = useState({
    title: event.title || "",
    description: event.description || "",
    clientId: event.clientId || "",
    projectManagerId: event.projectManagerId || "",
    accountManagerId: event.accountManagerId || "",
    adminId: event.adminId || "",
    start: event.start ? new Date(event.start).toISOString().slice(0, 16) : "",
    end: event.end ? new Date(event.end).toISOString().slice(0, 16) : ""
  });

  // Chargement des utilisateurs par rôle
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        
        const usersList = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Log pour vérifier les rôles
        console.log("Utilisateurs et rôles:", usersList.map(user => ({
          name: `${user.firstName} ${user.lastName}`,
          role: user.role
        })));
        
        setClients(usersList.filter(user => user.role === "client"));
        setProjectManagers(usersList.filter(user => user.role === "projectmanager"));
        // Utilisation du format avec tiret "charge-affaire"
        setAccountManagers(usersList.filter(user => user.role === "charge-affaire"));
        setAdmins(usersList.filter(user => user.role === "admin" || user.role === "superadmin"));
      } catch (error) {
        console.error("Erreur lors du chargement des utilisateurs:", error);
      }
    };
    
    if (isEditing) {
      fetchUsers();
    }
  }, [isEditing]);

  if (!event) return null;

  // Fonction pour formater les dates
  const formatDate = (dateValue) => {
    try {
      const date = typeof dateValue === 'object' && dateValue.toDate 
        ? dateValue.toDate() 
        : new Date(dateValue);
      
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error("Erreur de formatage de date:", error);
      return "Date non disponible";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleUpdate = async () => {
    try {
      const eventRef = doc(db, "events", event.id);
      
      const updatedData = {
        title: formData.title,
        description: formData.description,
        clientId: formData.clientId || null,
        projectManagerId: formData.projectManagerId || null,
        accountManagerId: formData.accountManagerId || null,
        adminId: formData.adminId || null,
        start: new Date(formData.start),
        end: formData.end ? new Date(formData.end) : null,
        updatedAt: new Date()
      };
      
      await updateDoc(eventRef, updatedData);
      
      // Mise à jour de l'UI
      if (onUpdate) {
        onUpdate({
          ...event,
          ...updatedData
        });
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'événement:", error);
      alert("Erreur lors de la mise à jour. Veuillez réessayer.");
    }
  };

  const handleDelete = async () => {
    if (!isDeleting) {
      setIsDeleting(true);
      return;
    }
    
    try {
      const eventRef = doc(db, "events", event.id);
      await deleteDoc(eventRef);
      
      // Fermer la modale après suppression
      onClose();
      
      // Rafraîchir le calendrier si nécessaire
      if (onUpdate) {
        onUpdate(null, true); // Le deuxième paramètre indique une suppression
      }
    } catch (error) {
      console.error("Erreur lors de la suppression de l'événement:", error);
      alert("Erreur lors de la suppression. Veuillez réessayer.");
      setIsDeleting(false);
    }
  };

  // Fonction pour obtenir le nom d'un utilisateur par son ID
  const getUserNameById = (id, userArray) => {
    const user = userArray.find(u => u.id === id);
    return user ? `${user.firstName} ${user.lastName}` : "Non assigné";
  };

  return (
    <div className="admin-event-details-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="modal-content bg-white rounded-lg p-6 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {!isEditing ? (
          <>
            <h2 className="text-xl font-bold mb-4">Détails de l'événement</h2>
            
            <div className="space-y-2 mb-4">
              <p><strong>Titre:</strong> {event.title || "Sans titre"}</p>
              <p><strong>Date de début:</strong> {event.start ? formatDate(event.start) : "Non spécifiée"}</p>
              {event.end && <p><strong>Date de fin:</strong> {formatDate(event.end)}</p>}
              
              {/* Afficher les participants à l'événement */}
              <div className="mt-4">
                <p className="font-medium">Participants :</p>
                <ul className="list-disc pl-5 mt-2">
                  {event.clientId && (
                    <li><span className="font-medium">Client :</span> {getUserNameById(event.clientId, clients)}</li>
                  )}
                  {event.projectManagerId && (
                    <li><span className="font-medium">Chef de projet :</span> {getUserNameById(event.projectManagerId, projectManagers)}</li>
                  )}
                  {event.accountManagerId && (
                    <li><span className="font-medium">Chargé d'affaire :</span> {getUserNameById(event.accountManagerId, accountManagers)}</li>
                  )}
                  {event.adminId && (
                    <li><span className="font-medium">Admin :</span> {getUserNameById(event.adminId, admins)}</li>
                  )}
                </ul>
              </div>
              
              {event.description && (
                <div className="mt-4">
                  <p className="font-medium">Description :</p>
                  <p className="mt-1">{event.description}</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-between">
              <div className="space-x-2">
                <button 
                  onClick={() => setIsEditing(true)} 
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded text-white transition-colors"
                >
                  Modifier
                </button>
                <button 
                  onClick={handleDelete} 
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded text-white transition-colors"
                >
                  {isDeleting ? "Confirmer" : "Supprimer"}
                </button>
              </div>
              <button 
                onClick={onClose} 
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800 transition-colors"
              >
                Fermer
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold mb-4">Modifier l'événement</h2>
            
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input 
                  type="text" 
                  name="title" 
                  value={formData.title} 
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <input 
                  type="datetime-local" 
                  name="start" 
                  value={formData.start} 
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                <input 
                  type="datetime-local" 
                  name="end" 
                  value={formData.end} 
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* Listes déroulantes pour les participants */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                  <select 
                    name="clientId" 
                    value={formData.clientId} 
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Non assigné</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.firstName} {client.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chef de projet</label>
                  <select 
                    name="projectManagerId" 
                    value={formData.projectManagerId} 
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Non assigné</option>
                    {projectManagers.map(pm => (
                      <option key={pm.id} value={pm.id}>
                        {pm.firstName} {pm.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chargé d'affaire</label>
                  <select 
                    name="accountManagerId" 
                    value={formData.accountManagerId} 
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Non assigné</option>
                    {accountManagers.map(am => (
                      <option key={am.id} value={am.id}>
                        {am.firstName} {am.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admin</label>
                  <select 
                    name="adminId" 
                    value={formData.adminId} 
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Non assigné</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>
                        {admin.firstName} {admin.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleChange}
                  rows="3"
                  className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                ></textarea>
              </div>
            </div>
            
            <div className="flex justify-between">
              <button 
                onClick={() => setIsEditing(false)} 
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800 transition-colors"
              >
                Annuler
              </button>
              <button 
                onClick={handleUpdate} 
                className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded text-white transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminEventDetails;